/** Drag-and-drop photo upload → Emergent object storage.
 *  value is "" | "/api/v1/files/..." | an external URL; onChange receives the same.
 *  A "paste a link" toggle keeps the old external-URL flow available.
 */
import { useRef, useState } from "react";
import { UploadCloud, X, Loader2, Link2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fileUrl } from "@/lib/files";
import { toast } from "sonner";

export default function PhotoDropzone({ value, onChange, testid = "photo-dropzone" }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const inputRef = useRef(null);

  function commitUrl() {
    const v = urlDraft.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) return; // partial typing — wait for a full URL
    onChange(v);
    setShowUrl(false);
    setUrlDraft("");
  }

  async function upload(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files, please.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photos must be under 8 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/v1/uploads/photo", fd);
      onChange(data.file.url);
      toast.success("Photo uploaded.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  }

  if (value) {
    return (
      <div className="relative border border-[color:var(--sepia)]/25" data-testid={testid}>
        <img src={fileUrl(value)} alt="Attached" className="w-full max-h-64 object-cover" />
        <button
          type="button"
          data-testid={`${testid}-remove`}
          onClick={() => onChange("")}
          className="absolute top-2 right-2 bg-[color:var(--parchment)] border border-[color:var(--sepia)]/40 p-1.5 hover:text-[color:var(--rust)]"
          title="Remove photo"
        >
          <X size={13} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid={testid}>
      <div
        role="button"
        tabIndex={0}
        data-testid={`${testid}-drop`}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          upload(e.dataTransfer.files?.[0]);
        }}
        className={`flex items-center justify-center gap-2 border border-dashed px-4 py-5 cursor-pointer text-sm transition-colors ${
          dragging
            ? "border-[color:var(--rust)] bg-[color:var(--rust)]/5 text-[color:var(--rust)]"
            : "border-[color:var(--sepia)]/50 text-[color:var(--sepia)] hover:bg-[color:var(--parchment-2)]/60"
        }`}
      >
        {uploading ? (
          <>
            <Loader2 size={15} className="animate-spin" /> Uploading…
          </>
        ) : (
          <>
            <UploadCloud size={15} /> Drop a photograph here, or click to choose
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => {
          upload(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => setShowUrl((s) => !s)}
        data-testid={`${testid}-url-toggle`}
        className="mt-1.5 inline-flex items-center gap-1 text-[11px] link-underline text-[color:var(--ink-soft)]"
      >
        <Link2 size={10} /> or paste a link
      </button>
      {showUrl && (
        <input
          data-testid={`${testid}-url`}
          value={urlDraft}
          onChange={(e) => setUrlDraft(e.target.value)}
          onBlur={commitUrl}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), commitUrl())}
          placeholder="https://… (press Enter)"
          className="mt-1 w-full bg-transparent border-b border-[color:var(--sepia)]/50 py-1.5 text-sm focus:outline-none focus:border-[color:var(--rust)]"
        />
      )}
    </div>
  );
}
